import { Collection, UserResponse } from "authory-api-types/dist/types";
import { useContext, useState } from "react";
import { DomainContext } from "../../context/domainContext";
import { scrollShadowHook } from "../../hooks/scrollShadowHook";
import { RequestHost } from "../../types/headers";
import { isMainDomain } from "../../utils/domainRouting";
import { getDisplayName } from "../../utils/getDisplayName";
import { V3ProfileAvatar } from "../ProfileAvatar";
import { V3ProfileContact, V3ProfileSocials } from "../ProfileHero/V3ProfileSocials";
import { OptionalLink } from "../ReusableStyledComponents/OptionalLink";
import { ContactUserFormik, V3ContactUser } from "../V3ContactUser";
import { PAHContainer, PAHLeftSide, PAHRightSide, PAHWrapper, V3PublicHeaderAuthorName } from "./V3PublicAuthorHeader.styles";
import { getAuthorPageUrl } from "../../utils/getAuthorPageUrl";
import { ProfileSocialsContainer } from "../ProfileHero/V4ProfileHero.styles";
import { getCoverInfo } from "../../utils/getCoverInfo";
import { getV4ColorVariant } from "../ProfileHero/V4ProfileHero.utils";
import { UserCoverStyle } from "authory-api-types/dist/enums";

interface V3PublicAuthorHeaderProps {
    user: UserResponse;
    onContactUserSubmit: (data: ContactUserFormik) => Promise<void>;
    nonSticky?: boolean;
    collection?: Collection;
}

export const V3PublicAuthorHeader = ({ user, onContactUserSubmit, nonSticky = false, collection }: V3PublicAuthorHeaderProps) => {
    // Contact modal
    const [contactModalOpen, setContactModalOpen] = useState(false);
    const [showShadow] = scrollShadowHook();

    // Current Domain
    const requestHostHeader: RequestHost = useContext(DomainContext) ?? RequestHost.MAINDOMAIN;

    // Background and colors
    const { color } = getCoverInfo(collection);
    const colorVariant = getV4ColorVariant(color, collection?.coverStyle || UserCoverStyle.none);

    return (
        <PAHWrapper $showShadow={showShadow} $nonSticky={nonSticky}>
            <PAHContainer>
                <PAHLeftSide>
                    <div>
                        <OptionalLink href={getAuthorPageUrl(user.displayAuthoryPage, isMainDomain(requestHostHeader), user.slug)}>
                            <V3ProfileAvatar size={"S"} src={user?.avatarUrl[0]?.url} authorName={getDisplayName(user)} />
                        </OptionalLink>
                    </div>
                    <div>
                        <OptionalLink href={getAuthorPageUrl(user.displayAuthoryPage, isMainDomain(requestHostHeader), user.slug)}>
                            <V3PublicHeaderAuthorName $colorVariant={colorVariant}>{getDisplayName(user)}</V3PublicHeaderAuthorName>
                        </OptionalLink>
                    </div>
                </PAHLeftSide>
                <PAHRightSide>
                    <div>
                        <ProfileSocialsContainer>
                            <div>
                                <V3ProfileSocials colorVariant={colorVariant} author={user} noOpacity={false} />
                            </div>
                            <div>
                                <V3ProfileContact
                                    openContactModal={() => setContactModalOpen(true)}
                                    colorVariant={colorVariant}
                                    author={user}
                                    noOpacity={false}
                                    isPublicAuthorHeader
                                />
                            </div>
                        </ProfileSocialsContainer>
                    </div>
                </PAHRightSide>
                {contactModalOpen && (
                    <V3ContactUser
                        modelOpen={contactModalOpen}
                        onCloseHandler={() => setContactModalOpen(false)}
                        userName={getDisplayName(user)}
                        onContactUserSubmit={onContactUserSubmit}
                    />
                )}
            </PAHContainer>
        </PAHWrapper>
    );
};
